import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, AdminStats, AdminUser, AdminFeedback, AdminContact, AdminSkill, AdminCosts, AdminUserDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import ConfirmModal from "@/components/ConfirmModal";

type Tab = "overview" | "users" | "feedback" | "contact" | "skills" | "costs";

const ADMIN_ROLES = ["admin:read", "admin:write"] as const;

const TYPE_BADGE: Record<string, string> = {
  bug: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  suggestion: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  general: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  other: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
};

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  scanning: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  reviewing: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  unpublished: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
};

// Skills-tab filter: "all", "default" (the default-skill set), or a status.
function matchesSkillFilter(s: AdminSkill, f: string): boolean {
  if (f === "all") return true;
  if (f === "default") return !!s.is_default;
  return s.status === f;
}

function roleBadge(r: string): string {
  if (r === "admin") return "bg-primary/15 text-primary";
  if (r.startsWith("admin")) return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
  return "bg-black/[0.05] text-text/60 dark:bg-white/10";
}

// Shared surfaces so every card / table reads as one system, matching the rest of the app.
const CARD = "rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)]";
const CARD_SHADOW = { boxShadow: "var(--shadow-card)" } as React.CSSProperties;
const TH = "px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-text/45";
const CHIP = "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium";

function fmt(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// The @username cell / link — stops the row click so it navigates to the public profile instead of
// opening the admin detail modal.
function UsernameLink({ username, className = "" }: { username: string | null; className?: string }) {
  if (!username) return <span className={`font-mono text-xs text-text/30 ${className}`}>—</span>;
  return (
    <Link
      to={`/u/${username}`}
      onClick={(e) => e.stopPropagation()}
      className={`font-mono text-xs font-medium text-primary transition hover:underline ${className}`}
      title={`View @${username}'s profile`}
    >
      @{username}
    </Link>
  );
}

export default function Admin() {
  const { user, accessToken, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [feedback, setFeedback] = useState<AdminFeedback[]>([]);
  const [contact, setContact] = useState<AdminContact[]>([]);
  const [skills, setSkills] = useState<AdminSkill[]>([]);
  const [skillFilter, setSkillFilter] = useState<string>("all");
  const [costs, setCosts] = useState<AdminCosts | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The skill whose default status is being confirmed (ConfirmModal open while set).
  const [confirmDefault, setConfirmDefault] = useState<AdminSkill | null>(null);

  const userRoles = user?.roles ?? [];
  const isAnyAdmin = userRoles.some(r => r === "admin" || r === "admin:read" || r === "admin:write");
  const isRootAdmin = userRoles.includes("admin");
  const canWrite = isRootAdmin || userRoles.includes("admin:write");

  useEffect(() => {
    if (!loading && (!user || !isAnyAdmin)) navigate("/", { replace: true });
  }, [loading, user, isAnyAdmin, navigate]);

  useEffect(() => {
    if (!accessToken || !isAnyAdmin) return;
    adminApi.stats(accessToken).then(setStats).catch(() => {});
  }, [accessToken, isAnyAdmin]);

  useEffect(() => {
    if (!accessToken || !isAnyAdmin) return;
    if (tab === "users" && users.length === 0)
      adminApi.listUsers(accessToken).then(setUsers).catch(() => {});
    if (tab === "feedback" && feedback.length === 0)
      adminApi.listFeedback(accessToken).then(setFeedback).catch(() => {});
    if (tab === "contact" && contact.length === 0)
      adminApi.listContact(accessToken).then(setContact).catch(() => {});
    if (tab === "skills" && skills.length === 0)
      adminApi.listSkills(accessToken).then(setSkills).catch(() => {});
    if (tab === "costs" && !costs)
      adminApi.getCosts(accessToken).then(setCosts).catch(() => {});
  }, [tab, accessToken, isAnyAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !user || !isAnyAdmin) return null;

  const token = accessToken!;

  async function toggleRole(u: AdminUser, role: string) {
    setBusy(true);
    setError("");
    try {
      if (u.roles.includes(role)) {
        await adminApi.revokeRole(u.id, role, token);
        setUsers(prev => prev.map(x => x.id === u.id ? { ...x, roles: x.roles.filter(r => r !== role) } : x));
      } else {
        await adminApi.grantRole(u.id, role, token);
        setUsers(prev => prev.map(x => x.id === u.id ? { ...x, roles: [...x.roles, role] } : x));
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleResolved(f: AdminFeedback) {
    setBusy(true);
    setError("");
    try {
      if (f.resolved) {
        await adminApi.unresolveFeedback(f.id, token);
        setFeedback(prev => prev.map(x => x.id === f.id ? { ...x, resolved: false } : x));
      } else {
        await adminApi.resolveFeedback(f.id, token);
        setFeedback(prev => prev.map(x => x.id === f.id ? { ...x, resolved: true } : x));
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleContactResolved(c: AdminContact) {
    setBusy(true);
    setError("");
    try {
      if (c.resolved) {
        await adminApi.unresolveContact(c.id, token);
        setContact(prev => prev.map(x => x.id === c.id ? { ...x, resolved: false } : x));
      } else {
        await adminApi.resolveContact(c.id, token);
        setContact(prev => prev.map(x => x.id === c.id ? { ...x, resolved: true } : x));
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function unpublish(s: AdminSkill) {
    if (!window.confirm(`Unpublish "${s.name}"? It will be hidden from the registry.`)) return;
    setBusy(true);
    setError("");
    try {
      await adminApi.unpublishSkill(s.slug, token);
      setSkills(prev => prev.map(x => x.id === s.id ? { ...x, status: "unpublished" } : x));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Default skills: every user gets these in their library (read-only) and on their machine.
  async function toggleDefault(s: AdminSkill) {
    const makeDefault = !s.is_default;
    setBusy(true);
    setError("");
    try {
      await adminApi.setDefaultSkill(s.slug, makeDefault, token);
      setSkills(prev => prev.map(x => x.id === s.id ? { ...x, is_default: makeDefault } : x));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
      setConfirmDefault(null);
    }
  }

  async function openUser(u: AdminUser) {
    setUserDetailLoading(true);
    setSelectedUser(null);
    try {
      const detail = await adminApi.getUser(u.id, token);
      setSelectedUser(detail);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUserDetailLoading(false);
    }
  }

  const TABS: { key: Tab; label: string; badge?: number }[] = [
    { key: "overview", label: "Overview" },
    { key: "users", label: "Users", badge: stats?.users_total },
    { key: "feedback", label: "Feedback", badge: stats?.feedback_unresolved || undefined },
    { key: "contact", label: "Contact", badge: stats?.contact_unresolved || undefined },
    { key: "skills", label: "Skills", badge: stats?.skills_pending || undefined },
    { key: "costs", label: "Costs" },
  ];

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-text/45">
            <span className="h-px w-5 bg-primary" /> Control room
          </p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-text">Admin Panel</h1>
        </div>
        <div className="flex gap-1.5">
          {userRoles.filter(r => r.startsWith("admin")).map(r => (
            <span key={r} className={`${CHIP} ${roleBadge(r)}`}>{r}</span>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/[0.08] px-3.5 py-2.5 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Tabs — underline style, consistent with the rest of the app */}
      <div className="mb-6 flex gap-6 overflow-x-auto border-b border-[var(--border)] text-sm">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 pb-2.5 font-medium transition ${
              tab === t.key ? "border-primary text-text" : "border-transparent text-text/50 hover:text-text/80"
            }`}
          >
            {t.label}
            {t.badge !== undefined && t.badge > 0 && (
              <span className={`rounded-full px-1.5 py-0.5 text-[11px] leading-none ${
                tab === t.key ? "bg-primary/15 text-primary" : "bg-black/[0.05] text-text/45 dark:bg-white/10"
              }`}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {[
            { label: "Total Users", value: stats.users_total },
            { label: "Live Skills", value: stats.skills_total },
            { label: "Skills Pending", value: stats.skills_pending },
            { label: "Suggestions", value: stats.suggestions_total },
            { label: "Feedback (total)", value: stats.feedback_total },
            { label: "Feedback (open)", value: stats.feedback_unresolved },
            { label: "Contact (total)", value: stats.contact_total },
            { label: "Contact (open)", value: stats.contact_unresolved },
          ].map(item => (
            <div key={item.label} className={`${CARD} px-5 py-4`} style={CARD_SHADOW}>
              <div className="font-display text-3xl font-bold tracking-tight text-text">{item.value}</div>
              <div className="mt-1 text-sm text-text/55">{item.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Users */}
      {tab === "users" && (
        <div className={`${CARD} overflow-hidden`} style={CARD_SHADOW}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg-subtle)]">
                  <th className={TH}>User</th>
                  <th className={TH}>Username</th>
                  {isRootAdmin && <th className={TH}>Roles</th>}
                  <th className={TH}>Joined</th>
                  {isRootAdmin && <th className={TH}>Grant / Revoke</th>}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="cursor-pointer border-t border-[var(--border)] transition hover:bg-[var(--bg-subtle)]" onClick={() => openUser(u)}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-text">{u.display_name || u.email.split("@")[0]}</div>
                      <div className="text-xs text-text/50">{u.email}</div>
                    </td>
                    <td className="px-4 py-3"><UsernameLink username={u.username} /></td>
                    {isRootAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.roles.map(r => (
                            <span key={r} className={`${CHIP} ${roleBadge(r)}`}>{r}</span>
                          ))}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-text/55">{fmt(u.created_at)}</td>
                    {isRootAdmin && (
                      <td className="px-4 py-3">
                        {u.id !== user.id && (
                          <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                            {ADMIN_ROLES.map(role => (
                              <button
                                key={role}
                                disabled={busy}
                                onClick={() => toggleRole(u, role)}
                                className={`rounded-lg border px-2 py-1 text-xs transition disabled:opacity-50 ${
                                  u.roles.includes(role)
                                    ? "border-red-400/40 text-red-500 hover:bg-red-500/10"
                                    : "border-[var(--border)] text-text/60 hover:border-primary/50 hover:text-text"
                                }`}
                              >
                                {u.roles.includes(role) ? `− ${role}` : `+ ${role}`}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {users.length === 0 && <p className="px-4 py-6 text-sm text-text/45">No users found.</p>}
        </div>
      )}

      {/* Feedback */}
      {tab === "feedback" && (
        <div className="space-y-3">
          {feedback.map(f => (
            <div key={f.id} className={`${CARD} p-4 transition-opacity ${f.resolved ? "opacity-50" : ""}`} style={f.resolved ? undefined : CARD_SHADOW}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className={`${CHIP} ${TYPE_BADGE[f.type] ?? TYPE_BADGE.general}`}>{f.type}</span>
                    <span className="text-xs text-text/50">{f.email}</span>
                    <span className="text-xs text-text/40">{fmt(f.created_at)}</span>
                    {f.resolved && <span className="text-xs font-medium text-green-600 dark:text-green-400">✓ resolved</span>}
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-text/80">{f.message}</p>
                </div>
                <button
                  disabled={busy}
                  onClick={() => toggleResolved(f)}
                  className="shrink-0 rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs text-text/60 transition hover:border-primary/50 hover:text-text disabled:opacity-50"
                >
                  {f.resolved ? "Reopen" : "Resolve"}
                </button>
              </div>
            </div>
          ))}
          {feedback.length === 0 && <p className="text-sm text-text/45">No feedback yet.</p>}
        </div>
      )}

      {/* Contact submissions */}
      {tab === "contact" && (
        <div className="space-y-3">
          {contact.map(c => (
            <div key={c.id} className={`${CARD} p-4 transition-opacity ${c.resolved ? "opacity-50" : ""}`} style={c.resolved ? undefined : CARD_SHADOW}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-text">{c.name}</span>
                    {c.company && <span className="text-xs text-text/50">· {c.company}</span>}
                    <a href={`mailto:${c.email}`} className="text-xs text-primary transition hover:underline">{c.email}</a>
                    <span className="text-xs text-text/40">{fmt(c.created_at)}</span>
                    {c.resolved && <span className="text-xs font-medium text-green-600 dark:text-green-400">✓ resolved</span>}
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-text/80">{c.message}</p>
                </div>
                <button
                  disabled={busy}
                  onClick={() => toggleContactResolved(c)}
                  className="shrink-0 rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs text-text/60 transition hover:border-primary/50 hover:text-text disabled:opacity-50"
                >
                  {c.resolved ? "Reopen" : "Resolve"}
                </button>
              </div>
            </div>
          ))}
          {contact.length === 0 && <p className="text-sm text-text/45">No contact submissions yet.</p>}
        </div>
      )}

      {/* Costs */}
      {tab === "costs" && (
        <div className="space-y-8">
          {!costs && <p className="text-sm text-text/45">Loading…</p>}
          {costs && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className={`${CARD} px-5 py-4`} style={CARD_SHADOW}>
                  <div className="font-display text-3xl font-bold tracking-tight text-text">${costs.total.cost_usd.toFixed(4)}</div>
                  <div className="mt-1 text-sm text-text/55">Total Cost (USD)</div>
                </div>
                <div className={`${CARD} px-5 py-4`} style={CARD_SHADOW}>
                  <div className="font-display text-3xl font-bold tracking-tight text-text">{costs.total.runs}</div>
                  <div className="mt-1 text-sm text-text/55">Total Agent Runs</div>
                </div>
              </div>

              <div>
                <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
                  <span className="h-px w-5 bg-primary" /> Cost by agent
                </p>
                <div className={`${CARD} overflow-hidden`} style={CARD_SHADOW}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border)] bg-[var(--bg-subtle)]">
                        <th className={TH}>Agent</th>
                        <th className={TH}>Runs</th>
                        <th className={`${TH} text-right`}>Cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costs.by_agent.map((a, i) => (
                        <tr key={a.agent} className={i !== 0 ? "border-t border-[var(--border)]" : ""}>
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-text">{a.agent}</td>
                          <td className="px-4 py-3 text-text/55">{a.runs}</td>
                          <td className="px-4 py-3 text-right font-medium text-text">${a.cost_usd.toFixed(4)}</td>
                        </tr>
                      ))}
                      {costs.by_agent.length === 0 && (
                        <tr><td colSpan={3} className="px-4 py-6 text-center text-text/45">No cost data yet — will appear after first skill publish or suggestion.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
                  <span className="h-px w-5 bg-primary" /> Cost by user
                </p>
                <div className={`${CARD} overflow-hidden`} style={CARD_SHADOW}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border)] bg-[var(--bg-subtle)]">
                        <th className={TH}>User</th>
                        <th className={TH}>Runs</th>
                        <th className={`${TH} text-right`}>Cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costs.by_user.map((u, i) => (
                        <tr key={u.user_id} className={i !== 0 ? "border-t border-[var(--border)]" : ""}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-text">{u.display_name || u.email.split("@")[0]}</div>
                            <div className="text-xs text-text/50">{u.email}</div>
                          </td>
                          <td className="px-4 py-3 text-text/55">{u.runs}</td>
                          <td className="px-4 py-3 text-right font-medium text-text">${u.cost_usd.toFixed(4)}</td>
                        </tr>
                      ))}
                      {costs.by_user.length === 0 && (
                        <tr><td colSpan={3} className="px-4 py-6 text-center text-text/45">No cost data yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Skills */}
      {tab === "skills" && (
        <div>
          <div className="mb-4 flex flex-wrap gap-2">
            {["all", "default", "active", "pending", "scanning", "reviewing", "rejected", "unpublished"].map(f => {
              const count = f === "all" ? skills.length : skills.filter(s => matchesSkillFilter(s, f)).length;
              return (
                <button
                  key={f}
                  onClick={() => setSkillFilter(f)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    skillFilter === f
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-[var(--border)] text-text/55 hover:text-text"
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>
          <div className={`${CARD} overflow-hidden`} style={CARD_SHADOW}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--bg-subtle)]">
                    <th className={TH}>Name</th>
                    <th className={TH}>Owner</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Layer</th>
                    <th className={TH}>Created</th>
                    {canWrite && <th className={TH}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {skills.filter(s => matchesSkillFilter(s, skillFilter)).map(s => (
                    <tr key={s.id} className="border-t border-[var(--border)] transition hover:bg-[var(--bg-subtle)]">
                      <td className="px-4 py-3">
                        <Link to={`/skill/${s.slug}`} className="font-medium text-text hover:text-primary hover:underline">{s.name || s.slug}</Link>
                        <div className="font-mono text-xs text-text/50">{s.slug}</div>
                        {s.is_default && (
                          <span className={`${CHIP} mt-1 bg-primary/15 text-primary`}>default</span>
                        )}
                        {/* A default users can't currently get — say why, so it isn't a silent gap. */}
                        {s.is_default && s.owner_can_own_defaults === false && (
                          <span className={`${CHIP} ml-1 mt-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300`} title="The owner is no longer admin / admin:write, so users don't get this default">
                            owner not admin — not shared
                          </span>
                        )}
                        {s.is_default && s.status !== "active" && (
                          <span className={`${CHIP} ml-1 mt-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300`} title="Only active skills reach users' libraries">
                            not active — not shared
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-text/85">{s.owner_name}</div>
                        <div className="text-xs text-text/50">{s.owner_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`${CHIP} ${STATUS_BADGE[s.status] ?? STATUS_BADGE.pending}`}>{s.status}</span>
                      </td>
                      <td className="px-4 py-3 text-text/55">L{s.layer}</td>
                      <td className="px-4 py-3 text-text/55">{fmt(s.created_at)}</td>
                      {canWrite && (
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            {(s.is_default || (s.status === "active" && s.visibility === "private" && s.owner_can_own_defaults)) && (
                              <button
                                disabled={busy}
                                onClick={() => setConfirmDefault(s)}
                                className="rounded-lg border border-primary/40 px-2.5 py-1 text-xs text-primary transition hover:bg-primary/10 disabled:opacity-50"
                              >
                                {s.is_default ? "Remove default" : "Make default"}
                              </button>
                            )}
                            {s.status !== "unpublished" && (
                              <button
                                disabled={busy}
                                onClick={() => unpublish(s)}
                                className="rounded-lg border border-red-400/40 px-2.5 py-1 text-xs text-red-500 transition hover:bg-red-500/10 disabled:opacity-50"
                              >
                                Unpublish
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {skills.filter(s => matchesSkillFilter(s, skillFilter)).length === 0 && (
              <p className="px-4 py-6 text-sm text-text/45">
                {skillFilter === "default" ? "No default skills." : "No skills with this status."}
              </p>
            )}
          </div>
        </div>
      )}

      {confirmDefault && (
        <ConfirmModal
          title={confirmDefault.is_default ? "Remove from default skills?" : "Make this a default skill?"}
          message={
            confirmDefault.is_default
              ? `"${confirmDefault.name}" leaves every user's library, and their machines on the next sync.`
              : `Every user gets "${confirmDefault.name}" in their library and on their machine, read-only.`
          }
          confirmLabel={confirmDefault.is_default ? "Remove default" : "Make default"}
          destructive={!!confirmDefault.is_default}
          busy={busy}
          onConfirm={() => toggleDefault(confirmDefault)}
          onCancel={() => setConfirmDefault(null)}
        />
      )}

      {/* User detail modal */}
      {(selectedUser || userDetailLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setSelectedUser(null)}>
          <div
            className="max-h-[85vh] w-full max-w-lg space-y-5 overflow-y-auto rounded-2xl p-6"
            style={{ background: "var(--bg-elev)", border: "1px solid var(--border)", boxShadow: "var(--shadow-lg, 0 20px 60px rgba(0,0,0,.3))" }}
            onClick={e => e.stopPropagation()}
          >
            {userDetailLoading && <p className="text-sm text-text/45">Loading…</p>}
            {selectedUser && (
              <>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold text-text">{selectedUser.display_name || selectedUser.email.split("@")[0]}</div>
                    <div className="text-sm text-text/55">{selectedUser.email}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <UsernameLink username={selectedUser.username} />
                      {selectedUser.username && (
                        <Link to={`/u/${selectedUser.username}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary transition hover:underline">
                          View profile
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                        </Link>
                      )}
                    </div>
                    {isRootAdmin && selectedUser.roles.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedUser.roles.map(r => (
                          <span key={r} className={`${CHIP} ${roleBadge(r)}`}>{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setSelectedUser(null)} className="text-xl leading-none text-text/40 transition hover:text-text">✕</button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Joined", value: fmt(selectedUser.created_at) },
                    { label: "Last Active", value: fmt(selectedUser.last_active) },
                    { label: "Agent Runs", value: selectedUser.agent_runs },
                    { label: "Cost Incurred", value: `$${selectedUser.cost_usd.toFixed(4)}` },
                  ].map(item => (
                    <div key={item.label} className="rounded-xl border border-[var(--border)] px-3 py-2.5">
                      <div className="text-sm font-semibold text-text">{item.value}</div>
                      <div className="mt-0.5 text-xs text-text/50">{item.label}</div>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="mb-2 text-sm font-semibold text-text">Skills Published ({selectedUser.skills.length})</div>
                  {selectedUser.skills.length === 0 ? (
                    <p className="text-xs text-text/45">None yet.</p>
                  ) : (
                    <div className="max-h-40 space-y-1 overflow-y-auto">
                      {selectedUser.skills.map(s => (
                        <div key={s.slug} className="flex items-center justify-between border-b border-[var(--border)] py-1 text-sm last:border-0">
                          <Link to={`/skill/${s.slug}`} className="font-medium text-text hover:text-primary hover:underline">{s.name}</Link>
                          <span className={`${CHIP} ${STATUS_BADGE[s.status] ?? STATUS_BADGE.pending}`}>{s.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between rounded-xl border border-[var(--border)] px-3 py-2.5 text-sm">
                  <span className="text-text/55">Suggestions submitted</span>
                  <span className="font-semibold text-text">{selectedUser.suggestions_count}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
